import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";
import { getOEMFranchisesLabels } from "@/Common/helpers/OEMs";
import { and, or } from "@/Common/helpers/sonar";
import { useValidations } from "@/Common/hooks/Validations";
import { IOEM } from "@/Common/interfaces/location";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import { NumberOrString } from "@/Layouts/components/InputComponents/InputValue/types";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import { IPvrTypeOption, PvrTypeInputConfig, PvrTypeOptions, TPvrType } from "@/Motivators/constants/PvrTypes";
import { Box, Collapse, Grid, Stack, SvgIcon, Tooltip, Typography } from "@mui/material";
import clsx from "clsx";
import { isEmpty } from "lodash";
import cloneDeep from "lodash-es/cloneDeep";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import { ITiers, TTierCommission } from "../../../../types/spiffSetup";
import { EActionType, TAction } from "../../reducer";
import { CONSTANTS } from "./constants";
import {
	defaultInput,
	getInputToUse,
	getLastIndex,
	isTiersMatrixValid,
	validateLastCommissionHighest
} from "./helpers";
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

const getFranchises = (oems: IOEM[] = []) => {
	if (!oems?.length) {
		return [];
	}

	const franchises = getOEMFranchisesLabels(oems).map((item: string) => {
		return {
			label: item,
		};
	});

	franchises.push({ label: "Pre-owned" });

	return franchises;
};

type EligibilityTiersProps = {
	readOnly?: boolean;
	type?: string;
	pvrType?: string;
	applyTiersRetroactively?: boolean;
	tiers: ITiers[];
	updateEligibilityRequirements: (updatedState: TAction) => void;
	oems?: IOEM[];
	franchises: string[];
};

const EligibilityTiers = (props: EligibilityTiersProps) => {
	const { readOnly, type, tiers, pvrType, applyTiersRetroactively, updateEligibilityRequirements, oems, franchises } = props;
	const [innerTiers, setInnerTiers] = useState<ITiers[]>(tiers);
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const [selectedPvrType, setSelectedPvrType] = useState<IPvrTypeOption | null>(() => {
		const defaultPvrType = pvrType || (type === "sales" ? TPvrType.FRONT_END_PVR : TPvrType.TRADES_PVR);
		return PvrTypeOptions.find((option) => option.value === defaultPvrType) || null;
	});

	useEffect(() => {
		if (selectedPvrType?.value === pvrType) return;
		const option = PvrTypeOptions.find((option) => option.value === pvrType) || null;
		setSelectedPvrType(option);
	}, [pvrType]);

	const inputConfig = useMemo(() => {
		const defaultConfig = PvrTypeInputConfig[TPvrType.FRONT_END_PVR];
		if (isEmpty(pvrType)) return defaultConfig;
		return PvrTypeInputConfig[pvrType as TPvrType] ?? defaultConfig;
	}, [pvrType]);

	const handleAddColumn = () => {
		const updatedTiers = cloneDeep(innerTiers);
		const columnInput = updatedTiers?.[0]?.["commissions"]?.[0]?.["from"]?.["input"];
		const commissionInput = getInputToUse(updatedTiers, 0);
		const lastIndex = getLastIndex(updatedTiers, "columns");
		updatedTiers.forEach((tier) => {
			tier.commissions.push({
				from: { value: 0, input: columnInput },
				to: { value: CONSTANTS.maximumColumnsValue, input: columnInput },
				commission: { value: 0, input: commissionInput }
			});
			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = CONSTANTS.minimumColumnsValue;
			}
		});
		update(updatedTiers);
	};

	const handleDeleteColumn = () => {
		const updatedTiers = cloneDeep(innerTiers);
		if (updatedTiers?.[0]?.["commissions"]?.length > CONSTANTS.minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = getLastIndex(tier?.commissions);
				tier.commissions[lastIndex]["to"]["value"] = CONSTANTS.maximumColumnsValue;
			});
		}
		update(updatedTiers);
	};

	const handleAddTier = () => {
		const updatedTiers = cloneDeep(innerTiers);
		const lastIndex = getLastIndex(updatedTiers, "length", 1);
		const commissionInput = getInputToUse(updatedTiers, lastIndex);
		const columnInput = updatedTiers?.[0]?.["commissions"]?.[0]?.["from"]?.["input"];
		const previousCommissionsLength = updatedTiers?.[lastIndex]?.["commissions"]?.length;

		const commissions = [];
		for (let i = 0; i < previousCommissionsLength; i++) {
			const previousFrom = updatedTiers?.[0]?.["commissions"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = updatedTiers?.[0]?.["commissions"]?.[i]?.["to"]?.["value"] || 0;
			commissions[i] = {
				from: { value: previousFrom, input: columnInput },
				to: { value: previousTo, input: columnInput },
				commission: { value: 0, input: commissionInput }
			};
		}

		let newRowFromValue = 0;
		updatedTiers[lastIndex].to.value = 0;
		updatedTiers.push({
			from: defaultInput(newRowFromValue, "Unit"),
			to: defaultInput(CONSTANTS.maximumUnits, "Unit"),
			commissions,
		});

		update(updatedTiers);
	};

	const handleDeleteTier = () => {
		const updatedTiers = cloneDeep(innerTiers);
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = getLastIndex(updatedTiers, "length", 1);
		updatedTiers[lastIndex].to.value = CONSTANTS.maximumUnits;
		update(updatedTiers);
	};

	const handleUpdateInput = (_row: number, _column: number, location: string, area: string, input: string) => {
		const updatedTiers = cloneDeep(innerTiers);
		switch (location) {
			case "column":
				break;
			case "row":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission: TTierCommission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(updatedTiers);
	};

	const handleUpdateValue = (row: number, column: number, location: string, area: string, value: NumberOrString) => {
		const updatedTiers = cloneDeep(innerTiers);
		const updatedValue = value ? Number(value) : 0;
		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission: TTierCommission, key: number) => {
						if (and(area in commission, key === column)) {
							commission[area]["value"] = updatedValue;
							if (tier.commissions?.[key + 1]) {
								tier.commissions[key + 1]["from"]["value"] = updatedValue + 0.01;
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "commission") {
					(updatedTiers as unknown as Record<string, TTierCommission>)[row][area]["value"] = updatedValue;
					if (and(!!updatedTiers?.[row + 1], area === "to")) {
						updatedTiers[row + 1]["from"]["value"] = updatedValue + 0.5;
					}
				} else {
					updatedTiers[row]["commissions"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}
		update(updatedTiers);
	};

	const update = (updatedTiers: ITiers[]) => {
		setInnerTiers(updatedTiers);
	};

	const handleChangePvrType = (selected: TSelectValue) => {
		setSelectedPvrType(selected as IPvrTypeOption);
		const pvrUpdatedValue = (selected as { value: string }).value;
		const updatedTiers = cloneDeep(innerTiers);

		const newConfig = PvrTypeInputConfig[pvrUpdatedValue as TPvrType];
		const newColumnInputType = newConfig?.columnInput?.type;
		const newCommissionInputType = newConfig?.bonusInput?.[0]?.type;
		const MINIMUM_NEGATIVE = -100000000;
		const minFromValue = pvrUpdatedValue === TPvrType.TRADES_ACV ? 0 : MINIMUM_NEGATIVE;

		updatedTiers.forEach((tier) => {
			tier.commissions.forEach((commission, index) => {
				const isFirstColumn = index === 0;
				const isLastColumn = index === tier.commissions.length - 1;
				commission.from.input = newColumnInputType;
				commission.from.value = isFirstColumn ? minFromValue : 0;
				commission.to.input = newColumnInputType;
				commission.to.value = isLastColumn ? CONSTANTS.maximumColumnsValue : 0;
				commission.commission.value = 0;
				commission.commission.input = newCommissionInputType;
			});
		});

		update(updatedTiers);
		updateEligibilityRequirements({
			type: EActionType.UPDATE_PVR_TYPE,
			payload: { pvrType: pvrUpdatedValue, tiers: updatedTiers },
		});
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "units",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0.5 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "pvr",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid column tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
									constraints: [
										{
											on: "commission",
											apply: true,
										},
									],
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper > lower is good,
									deltas: { Dollar: 0, "Dollar per Unit": 0 },
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect spiff commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerTiers, validate]);

	const unitsErrors = errors?.get("units");
	const pvrErrors = errors?.get("pvr");
	const commissionsErrors = errors?.get("commissions");
	const isLastCommissionValid = validateLastCommissionHighest(innerTiers);
	const isValidTiersMatrixLength = isTiersMatrixValid(innerTiers);

	const isHighestCommissionInValid = and(!isLastCommissionValid, innerTiers?.length === 1, innerTiers[0]?.commissions?.length === 1);
	if (isHighestCommissionInValid) {
		localErrorsMessages.push("Highest commission must be not equal to zero.");
	}

	if (or(!isLastCommissionValid, !isValidTiersMatrixLength)) {
		localErrorsMessages.push("Incorrect spiff commissions definition");
	}

	useEffect(() => {
		if (isLastCommissionValid && isValid && isValidTiersMatrixLength) {
			updateEligibilityRequirements({ type: EActionType.UPDATE_TIER, payload: { tiers: innerTiers } });
		}
	}, [innerTiers]);

	useEffect(() => {
		if (isEmpty(pvrType)) {
			const defaultPvrType = type === "sales" ? TPvrType.FRONT_END_PVR : TPvrType.TRADES_PVR;
			const defaultOption = PvrTypeOptions.find((option) => option.value === defaultPvrType) || null;
			
			setSelectedPvrType(defaultOption);
			
			updateEligibilityRequirements({
				type: EActionType.UPDATE_PVR_TYPE,
				payload: { pvrType: defaultPvrType },
			});
		}
	}, [pvrType, type]);

	const renderTitleComponent = useCallback(() => {
		const spiffType = type?.toUpperCase() ?? "";
		const filteredOptions = PvrTypeOptions.filter((option: IPvrTypeOption) => option.spiffType === spiffType) ?? [];

		switch (spiffType) {
			case "SALES":
			case "TRADES":
				return (
					<SmartSelector
						classes="-mt-[3px]"
						selectBy="value"
						showBy="label"
						options={filteredOptions}
						selected={selectedPvrType}
						listBoxProps={{
							sx: { maxHeight: "223px" },
						}}
						popperProps={{ className: "!z-[1350]" }}
						sx={{
							width: 160,
							height: 30,
						}}
						onChangeAction={handleChangePvrType}
					/>
				);
			default:
				return (
					<Stack display="flex" direction="row" alignItems="center" gap={"2px"}>
						<Typography className="!text-base !text-default !font-montserrat !font-medium">{pvrType}</Typography>
						<Tooltip
							arrow
							placement="top"
							title={
								<Typography className="!text-sm !text-white !font-sans !font-thin" component="span">
									Ascending Amounts
								</Typography>
							}
						>
							<Box>
								<SvgIcon
									className="!w-[14px] !h-[14px] !fill-default !-mt-[10px]"
									component={InformationIcon}
									viewBox="0 0 24 24"
								/>
							</Box>
						</Tooltip>
					</Stack>
				);
		}
	}, [pvrType, type]);

	const handleCheck = (checked: boolean) => {
		updateEligibilityRequirements({ type: EActionType.UPDATE_APPLY_RETROACTIVELY, payload: { applyTiersRetroactively: checked } });
	};

	const handleFranchiseChange = (value: TSelectValue) => {
		let updatedFranchises: string[] = [];
		if (!value) {
			return;
		}
		if (typeof value === "string") {
			updatedFranchises = [value];
		}
		if (Array.isArray(value)) {
			updatedFranchises = value.map((item) => item.label);
		}

		updateEligibilityRequirements({
			type: EActionType.UPDATE_FRANCHISES,
			payload: { franchises: updatedFranchises },
		});
	};

	useEffect(() => {
		if (!franchises?.length) {
			const franchisesLabels = getOEMFranchisesLabels(oems);
			const defaultFranchisesSelected = [...franchisesLabels, "Pre-owned"];
			updateEligibilityRequirements({
				type: EActionType.UPDATE_FRANCHISES,
				payload: { franchises: defaultFranchisesSelected },
			});
		}
	}, []);

	return (
		<>
			<Box
				className={clsx("flex flex-row items-center my-8", {
					"justify-between": type === "trades",
					"justify-end": type === "sales",
				})}
			>
				{type === "trades" && (
					<SmartSelector
						multiple
						multipleSelectAll
						tagsVariant="text"
						classes="!w-[150px]"
						showBy="label"
						selectBy="label"
						options={getFranchises(oems) ?? []}
						selected={franchises?.map((item: string) => ({ label: item }))}
						selectDefault={true}
						inputProps={{
							placeholder: "Franchise(s)",
							hideTooltip: true,
						}}
						popperProps={{ className: "!z-[1350]" } as object}
						hideTooltip={true}
						onChangeAction={(value) => handleFranchiseChange(value)}
						error={!franchises?.length}
					/>
				)}

				<CustomizedSwitches
					textUnChecked="Apply Retroactively"
					textChecked="Apply Retroactively"
					onChange={handleCheck}
					checked={!!applyTiersRetroactively}
					className="!ml-[15px] !mb-[24px]"
				/>
			</Box>
			<Box mt={2}>
				<Grid item xs={12}>
					<TierColumns
						titleLabel={renderTitleComponent()}
						titleContainerProps={{
							justifyContent: "flex-start",
							alignItems: "end",
							sx: { maxWidth: "180px", width: "180px", marginRight: "3px", marginLeft: "30px" },
						}}
						titleProps={{
							className: "tiered-title tiered-title-adjustment",
							sx: null,
						}}
						disabledDelete={or(innerTiers?.[0]?.commissions?.length <= CONSTANTS.minimumColumns, !!readOnly)}
						disabledAdd={or(innerTiers?.[0]?.commissions?.length >= CONSTANTS.maximumColumns, !!readOnly)}
						onClickDelete={handleDeleteColumn}
						onClickAdd={handleAddColumn}
					>
						<Stack direction={"row"} position={"relative"}>
							{innerTiers?.[0]?.commissions?.map((tier: TTierCommission, key: number) => {
								const keyIndex = `commission_matrix_column_${key}`;
								const fromDisabled = Boolean(!key);
								const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);
								return (
									<TierColumnsItem key={keyIndex} index={key} title={"Spiff"}>
										<MatrixColumn
											index={key}
											item={tier}
											fromDisabled={fromDisabled}
											toDisabled={toDisabled}
											onActionUpdate={handleUpdateValue}
											pvrErrors={pvrErrors}
											inputDefinition={inputConfig?.columnInput}
											inputSx={inputConfig?.columnInputSx}
										/>
									</TierColumnsItem>
								);
							})}
						</Stack>
					</TierColumns>
				</Grid>
				<Grid item xs={12} mt={1}>
					<TierRows
						showTitle
						title={"Units"}
						sxTitlePropsContainer={{
							top: 102,
							left: 30,
						}}
						titleProps={{
							className: "tiered-title",
							sx: { transform: "rotate(270deg)" },
							icon: {
								present: true,
								text: "Ascending Amounts",
							},
						}}
						centerTitleAfterRow={1}
						deleteButtonText={"Remove Tier"}
						addButtonText={"Add Tier"}
						deleteButtonDisabled={or(innerTiers?.length <= 1, !!readOnly)}
						addButtonDisabled={or(innerTiers?.length >= CONSTANTS.maximumTiers, !!readOnly)}
						spaces={{
							mainSection: 230,
							columns: 210,
							totalColumns: innerTiers?.[0]?.commissions?.length || 1,
							totalRows: innerTiers?.length || 1,
							separation: 5.5,
						}}
						rowsAreaProps={{
							ml: "35px",
						}}
						onClickDelete={handleDeleteTier}
						onClickAdd={handleAddTier}
					>
						<TransitionGroup>
							<Collapse>
								<Stack direction="column" rowGap={2}>
									{(innerTiers || [])?.map((tier, key) => {
										const keyIndex = `commission_matrix_row_${key}`;
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										return (
											<Grid key={keyIndex} container item>
												<MatrixRow
													index={key}
													item={tier}
													spaces={{
														mainSection: 230,
														columns: 242,
														totalColumns: innerTiers?.[0]?.commissions?.length || 1,
														totalRows: innerTiers?.length || 1,
														separation: 5.5,
														reduction: 115.5,
													}}
													spacing={"9px"}
													sxMainContent={{ minWidth: "230px", marginRight: "3px" }}
													sxCells={{ minWidth: "248px" }}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													onChangeType={handleUpdateInput}
													onActionUpdate={handleUpdateValue}
													unitsErrors={unitsErrors}
													commissionsErrors={commissionsErrors}
													isLastCommissionValid={isLastCommissionValid}
													rowsLength={innerTiers.length}
													bonusInputDefinitions={inputConfig?.bonusInput}
													inputSx={inputConfig?.bonusInputSx}
												/>
											</Grid>
										);
									})}
								</Stack>
							</Collapse>
						</TransitionGroup>
						<Grid container direction="column" rowSpacing={0.2} mt={1} mb={1} alignItems="flex-start">
							{Boolean(Array.from(new Set(localErrorsMessages))?.length) && (
								<>
									{Array.from(new Set(localErrorsMessages)).map((message, key) => {
										const indexKey = `error_commission_matrix_${message}_${key}`;
										return <TierAlertError key={indexKey} text={message} />;
									})}
								</>
							)}
						</Grid>
					</TierRows>
				</Grid>
			</Box>
		</>
	);
};

export default EligibilityTiers;
